import React from "react";
import { uniqueId } from "lodash";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import { PdImagePicker, tPdImagePicker, tPdImagePickerAction } from "src/components/forms/picker";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useController, UseControllerProps, useFormContext } from "react-hook-form";

export type tImagePickerValue = {
  uri?: string;
  file?: File;
  action?: tPdImagePickerAction;
};

export type tPdMultipleImagePicker = Omit<tPdImagePicker, "source" | "onChange"> & {
  contentContainerStyle?: StyleProp<ViewStyle> | undefined;
  value?: tImagePickerValue[];
  onChange?: (value: tImagePickerValue[]) => void;
  length?: number;
};

export const PdMultipleImagePicker: React.FC<tPdMultipleImagePicker> = ({
  style,
  contentContainerStyle,
  value = [],
  onChange,
  length = 3,
  ...rest
}) => {
  const canAdd = value.length < length;

  const valueWithIds: ({ id: string } & tImagePickerValue)[] = React.useMemo(
    () => value.map((valueItem) => ({ id: uniqueId("multiple_image_picker_"), ...valueItem })),
    [value],
  );

  const handleOnChange = async (multiplePickerOption: { value: tImagePickerValue; index: number }) => {
    let newValue: tImagePickerValue[] = [];

    if (multiplePickerOption.value.action === "change") {
      newValue = value.map((valueItem, valueIndex) =>
        valueIndex === multiplePickerOption.index ? multiplePickerOption.value : valueItem,
      );
      onChange?.(newValue);
    } else if (multiplePickerOption.value.action === "remove") {
      newValue = value
        .filter((_valueItem, valueIndex) => valueIndex !== multiplePickerOption.index)
        .map((valueItem) => ({ uri: valueItem.uri, file: valueItem.file, action: multiplePickerOption.value.action }));
    }

    onChange?.(newValue);
  };

  const handleOnAdd: tPdImagePicker["onChange"] = ({ uri, file, action }) => {
    if (action === "change") {
      onChange?.([...value, { uri, file, action }]);
    }
  };

  return (
    <View style={[styles.imagePickerContainer, style]}>
      {valueWithIds.map((valueItem, valueIndex) => (
        <PdImagePicker
          key={valueItem.id}
          style={styles.imagePicker}
          mobileStyle={styles.imagePicker}
          webStyle={webStyles.imagePicker}
          source={valueItem}
          onChange={(pickerValue) => handleOnChange({ value: pickerValue, index: valueIndex })}
          allowDelete
          {...rest}
        />
      ))}
      {canAdd && (
        <PdImagePicker
          style={styles.imagePicker}
          mobileStyle={styles.imagePicker}
          webStyle={webStyles.imagePicker}
          onChange={handleOnAdd}
        />
      )}
      {Array.from({ length: length - value.length - 1 }).map((_, index) => (
        <View style={styles.imagePickerBlank} key={index} />
      ))}
    </View>
  );
};

export const PdHookFormMultipleImagePicker: React.FC<
  Pick<UseControllerProps, "name" | "rules"> & tPdMultipleImagePicker
> = (props) => {
  const { name, rules, onChange, ...rest } = props;
  const { control } = useFormContext();
  const { field } = useController({ name, rules, control });

  const handleOnValueChange: tPdMultipleImagePicker["onChange"] = (value) => {
    field.onChange(value);
    onChange?.(value);
  };

  return <PdMultipleImagePicker {...rest} value={field.value} onChange={handleOnValueChange} />;
};

const styles = StyleSheet.create({
  imagePicker: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
  },
  imagePickerBlank: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.VeryLightGrey,
    borderRadius: kStyleConst.kSpacing,
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
  },
  imagePickerContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    margin: -kStyleConst.kSpacing,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  imagePicker: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
  },
} as const;
